import React, { useState, useEffect } from 'react';
import { Search, X, ArrowRight, Clock, Star, Wrench, Award, FileText, Calendar } from 'lucide-react';
import { mockData } from '../mock';
import { Link } from 'react-router-dom';

const HomeSearch = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Create searchable data from all sections
  const createSearchData = () => {
    const searchData = [];
    
    try {
      // Add services
      if (mockData.services) {
        mockData.services.forEach(service => {
          searchData.push({
            id: `service-${service.title}`,
            title: service.title,
            description: service.description,
            type: 'Service',
            icon: Wrench,
            link: '/services',
            category: 'Services',
            tags: ['automotive', 'detailing', 'protection', service.title.toLowerCase()]
          });
        });
      }

      // Add training programs
      if (mockData.training && mockData.training.programs) {
        mockData.training.programs.forEach(program => {
          const programTags = ['training', 'certification', 'education', program.name.toLowerCase()];
          
          // Add specific tags based on program content
          if (program.name.toLowerCase().includes('installation')) {
            programTags.push('installation', 'ppf installation', 'sunfilm installation', 'installation training', 'ppf training', 'sunfilm training');
          }
          if (program.name.toLowerCase().includes('detailing')) {
            programTags.push('detailing', 'professional detailing', 'detailing certification', 'detailing training');
          }
          if (program.name.toLowerCase().includes('rupes')) {
            programTags.push('rupes', 'polishing', 'rupes training', 'polishing training', 'rupes certification');
          }
          
          // Add module-based tags
          if (program.modules) {
            program.modules.forEach(module => {
              const moduleLower = module.toLowerCase();
              programTags.push(moduleLower);
              if (moduleLower.includes('ppf')) programTags.push('ppf', 'paint protection film');
              if (moduleLower.includes('sunfilm')) programTags.push('sunfilm', 'window tinting', 'solar film');
              if (moduleLower.includes('ceramic')) programTags.push('ceramic coating', 'ceramic');
              if (moduleLower.includes('paint')) programTags.push('paint correction', 'paint');
            });
          }
          
          searchData.push({
            id: `training-${program.name}`,
            title: program.name,
            description: program.description,
            type: 'Training',
            icon: Award,
            link: '/training',
            category: 'Training',
            tags: programTags,
            price: program.price,
            duration: program.duration
          });
        });
      }

      // Add tuning services
      if (mockData.tuning && mockData.tuning.services) {
        mockData.tuning.services.forEach(service => {
          const tuningTags = ['tuning', 'performance', 'automotive', service.name.toLowerCase()];
          
          // Add specific tags based on service name
          if (service.name.toLowerCase().includes('speed chime')) {
            tuningTags.push('speed', 'chime', 'removal', 'speed chime', 'chime removal', 'speed warning');
          }
          if (service.name.toLowerCase().includes('ecu')) {
            tuningTags.push('ecu', 'remapping', 'ecu remapping', 'engine management');
          }
          if (service.name.toLowerCase().includes('performance')) {
            tuningTags.push('performance tuning', 'power', 'enhancement');
          }
          if (service.name.toLowerCase().includes('diagnostic')) {
            tuningTags.push('diagnostic', 'diagnostics', 'troubleshooting', 'analysis');
          }
          
          searchData.push({
            id: `tuning-${service.name}`,
            title: service.name,
            description: service.description,
            type: 'Tuning',
            icon: Wrench,
            link: '/services',
            category: 'Tuning Services',
            tags: tuningTags,
            price: service.price,
            popular: service.popular
          });
        });
      }

      // Add blog posts
      if (mockData.blogs) {
        mockData.blogs.forEach(post => {
          searchData.push({
            id: `blog-${post.id}`,
            title: post.title,
            description: post.excerpt,
            type: 'Article',
            icon: FileText,
            link: '/blog',
            category: 'Blog',
            tags: ['article', 'blog', 'information', post.title.toLowerCase()],
            author: post.author,
            readTime: post.readTime
          });
        });
      }

      // Add other important pages
      const additionalPages = [
        {
          id: 'about',
          title: 'About Nirmal Narayan',
          description: '25+ years of automotive expertise and founder of Unisol Global',
          type: 'Page',
          icon: Award,
          link: '/about',
          category: 'Company',
          tags: ['about', 'founder', 'experience', 'nirmal']
        },
        {
          id: 'ppf-story',
          title: 'PPF Development Story',
          description: 'Learn about our Paint Protection Film developed specifically for Indian conditions',
          type: 'Page',
          icon: FileText,
          link: '/ppf',
          category: 'Technology',
          tags: ['ppf', 'paint protection film', 'development', 'story', 'indian conditions', 'technology', 'manufacturing']
        },
        {
          id: 'gallery',
          title: 'Gallery & Portfolio',
          description: 'Showcase of our premium automotive detailing work',
          type: 'Page',
          icon: Star,
          link: '/gallery',
          category: 'Portfolio',
          tags: ['gallery', 'portfolio', 'work', 'showcase']
        },
        {
          id: 'ewarranty',
          title: 'e-Warranty Registration',
          description: 'Register and manage your automotive service warranties',
          type: 'Service',
          icon: FileText,
          link: '/e-warranty',
          category: 'Warranty',
          tags: ['warranty', 'registration', 'ewarranty', 'protection']
        },
        {
          id: 'contact',
          title: 'Contact Us',
          description: 'Get in touch with our automotive experts',
          type: 'Page',
          icon: Calendar,
          link: '/contact',
          category: 'Contact',
          tags: ['contact', 'support', 'location', 'phone']
        }
      ];

      searchData.push(...additionalPages);
    } catch (error) {
      console.error('Error creating search data:', error);
    }
    
    return searchData;
  };

  const searchData = createSearchData();

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    
    // Simulate search delay for better UX
    const searchTimeout = setTimeout(() => {
      const query = searchQuery.toLowerCase().trim();
      const searchTerms = query.split(' ').filter(term => term.length > 0);
      
      const results = searchData.filter(item => {
        // Single term search
        if (searchTerms.length === 1) {
          return (
            item.title.toLowerCase().includes(query) ||
            item.description.toLowerCase().includes(query) ||
            item.category.toLowerCase().includes(query) ||
            item.tags.some(tag => tag.includes(query))
          );
        }
        
        // Multi-term search - all terms must match somewhere
        return searchTerms.every(term => {
          return (
            item.title.toLowerCase().includes(term) ||
            item.description.toLowerCase().includes(term) ||
            item.category.toLowerCase().includes(term) ||
            item.tags.some(tag => tag.includes(term))
          );
        });
      });

      // Enhanced relevance sorting
      const sortedResults = results.sort((a, b) => {
        const aTitle = a.title.toLowerCase();
        const bTitle = b.title.toLowerCase();
        
        // Exact phrase match in title gets highest priority
        if (aTitle.includes(query) && !bTitle.includes(query)) return -1;
        if (!aTitle.includes(query) && bTitle.includes(query)) return 1;
        
        // Multi-term relevance scoring
        if (searchTerms.length > 1) {
          const aScore = searchTerms.reduce((score, term) => {
            if (aTitle.includes(term)) score += 3;
            if (a.description.toLowerCase().includes(term)) score += 2;
            if (a.category.toLowerCase().includes(term)) score += 2;
            if (a.tags.some(tag => tag.includes(term))) score += 1;
            return score;
          }, 0);
          
          const bScore = searchTerms.reduce((score, term) => {
            if (bTitle.includes(term)) score += 3;
            if (b.description.toLowerCase().includes(term)) score += 2;
            if (b.category.toLowerCase().includes(term)) score += 2;
            if (b.tags.some(tag => tag.includes(term))) score += 1;
            return score;
          }, 0);
          
          if (aScore !== bScore) return bScore - aScore;
        }
        
        // Single term exact title matches
        const aExactTitle = aTitle.includes(query);
        const bExactTitle = bTitle.includes(query);
        
        if (aExactTitle && !bExactTitle) return -1;
        if (!aExactTitle && bExactTitle) return 1;
        
        // Prioritize popular services
        if (a.popular && !b.popular) return -1;
        if (!a.popular && b.popular) return 1;
        
        return 0;
      });

      setSearchResults(sortedResults.slice(0, 8)); // Limit to 8 results
      setIsSearching(false);
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [searchQuery]);

  const handleSearchClick = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Service': return 'bg-blue-100 text-blue-800';
      case 'Training': return 'bg-green-100 text-green-800';
      case 'Tuning': return 'bg-purple-100 text-purple-800';
      case 'Article': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <>
      {/* Search Button */}
      <div className="flex justify-center mt-8 mb-4">
        <button
          onClick={handleSearchClick}
          className="bg-white/90 backdrop-blur-sm border-2 border-gray-200 text-gray-700 px-6 py-3 rounded-xl font-medium hover:border-blue-300 hover:text-blue-600 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center space-x-3 group"
        >
          <Search className="w-5 h-5 group-hover:text-blue-600 transition-colors" />
          <span>Search services, training, and more...</span>
        </button>
      </div>

      {/* Search Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center pt-20">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden">
            {/* Search Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center space-x-4">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search for services, training programs, articles..."
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-lg"
                    autoFocus
                  />
                </div>
                <button
                  onClick={handleClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Search Results */}
            <div className="max-h-96 overflow-y-auto">
              {searchQuery.trim() === '' ? (
                // Popular searches when no query
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Popular Searches</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {['PPF Training', 'Speed Chime Removal', 'Paint Protection Film', 'Ceramic Coating', 'Gallery', 'e-Warranty'].map(item => (
                      <button
                        key={item}
                        onClick={() => setSearchQuery(item)}
                        className="text-left p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium text-gray-700"
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
              ) : isSearching ? (
                // Loading state
                <div className="p-6 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-500 mt-2">Searching...</p>
                </div>
              ) : searchResults.length > 0 ? (
                // Search results
                <div className="p-2">
                  {searchResults.map((result) => {
                    const IconComponent = result.icon;
                    return (
                      <Link
                        key={result.id}
                        to={result.link}
                        onClick={handleClose}
                        className="flex items-center p-4 hover:bg-gray-50 rounded-xl transition-colors group"
                      >
                        <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                          <IconComponent className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                              {result.title}
                            </h4>
                            {result.popular && (
                              <Star className="w-4 h-4 text-yellow-500 fill-current" />
                            )}
                          </div>
                          <p className="text-sm text-gray-600 line-clamp-2">
                            {result.description}
                          </p>
                          <div className="flex items-center space-x-2 mt-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(result.type)}`}>
                              {result.category}
                            </span>
                            {result.price && (
                              <span className="text-xs text-gray-500">
                                {result.price}
                              </span>
                            )}
                            {result.readTime && (
                              <span className="text-xs text-gray-500 flex items-center">
                                <Clock className="w-3 h-3 mr-1" />
                                {result.readTime}
                              </span>
                            )}
                          </div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                      </Link>
                    );
                  })}
                </div>
              ) : (
                // No results
                <div className="p-6 text-center">
                  <div className="text-gray-400 mb-4">
                    <Search className="w-12 h-12 mx-auto" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
                  <p className="text-gray-600 mb-4">
                    Try searching for services like "PPF", "training", "ceramic coating", or "tuning"
                  </p>
                  <button
                    onClick={() => setSearchQuery('')}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Clear search
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HomeSearch;